import { useCallback, useEffect, useMemo, useState } from 'react';
import { Doc, DocSearchFilter, FirebaseMessage } from '../../../../types';
import {
  startDriveSearchTask,
  startUserDriveSearchTask,
} from '../../../api/users-api';
import { useMutation } from '@tanstack/react-query';
import DriveSearchGrid from './drive-search-grid';
import { handleErrors } from '../../../utils/errors';
import { useForm } from 'react-hook-form';
import { useStore } from '../../state/zustand-store';
import useFirebaseMessageHandler from '../../../shared/firebase/useFirebaseMessageHandler';
import DriveSearchGridFilters from './drive-search-grid-filters';
import { pagesConfig } from '../../../shared/pages-config';
import SharedErrorBoundary from '../../../shared/shared-error-boundary';
import { useUserSettings } from '../../../utils/use-user-settings';
import { Messages } from '../../../utils/message-constants';
import SimpleBreadcrumbsPage from '../../../shared/simple-breadcrumbs-page';
import { ModuleList } from '../../../shared/analytics/module-list';
import { useParams } from 'react-router-dom';
import { useAnalytics } from '../../../shared/analytics/create-events';
import * as analytics from '../../../shared/analytics/analytics.json';
import Stack from '@mui/material/Stack';

export interface DriveSearchForm extends DocSearchFilter {
  dateType?: string;
  time?: string;
  day?: Date | null;
}

export default function DriveSearchPage() {
  const { userEmail } = useParams() as { userEmail: string };

  const fromUserPage = !!userEmail;

  const { primaryDomain, driveRoles, changeLogRoles, loggedInUser } =
    useUserSettings();

  const { createEvent } = useAnalytics();

  const { setSnackbar, firebaseUser } = useStore();
  const [searchedDocs, setSearchedDocs] = useState<Doc[]>([]);
  const [clickedDoc, setClickedDoc] = useState<Doc | null>(null);
  const [isLoading, setIsLoading] = useState(false);
  const [erroredUsers, setErroredUsers] = useState<string[]>([]);
  const [numberOfSearchedUsers, setNumberOfSearchedUsers] = useState(0);

  const defaultValues: Partial<DriveSearchForm> = useMemo(
    () => ({
      trashed: false,
      starred: false,
      hidden: false,
      onlySharedWithMe: false,
      modifiedDate: null,
      lastViewedByMeDate: null,
      onlyOwnedByMe: false,
      isExactTitle: false,
      allUsers: false,
      isSuspended: false,
      sourceType: 'User',
      title: '',
      mimeType: 'all',
      docID: '',
      sources: fromUserPage ? [userEmail] : [],
      parents: [],
      owners: [],
      writers: [],
      readers: [],
      dateType: 'modifiedDate',
      time: '>',
      day: null,
      labels: [],
    }),
    [fromUserPage, userEmail]
  );

  const {
    control,
    handleSubmit,
    formState: { errors },
    watch,
    setValue,
    getValues,
    trigger,
    reset,
  } = useForm<Partial<DriveSearchForm>>({
    defaultValues,
  });

  const firebaseMessageType = fromUserPage
    ? (`driveSearchUserCore-${userEmail}` as const)
    : ('driveSearchCore' as const);

  const startSearchFn = fromUserPage
    ? startUserDriveSearchTask
    : startDriveSearchTask;

  const { mutate, isPending } = useMutation({
    mutationFn: ({
      data,
      changeLogMessage,
    }: {
      data: Partial<DocSearchFilter>;
      changeLogMessage: string;
    }) =>
      startSearchFn(
        data,
        firebaseUser.firebaseDbPath,
        false,
        changeLogMessage,
        firebaseMessageType
      ),
    onSuccess: () => {
      setSnackbar({
        open: true,
        severity: 'info',
        message: 'Searching for documents...',
      });
    },
    onError: (error) => {
      handleErrors({
        error,
        snackbarMessage: Messages.Errors.Network.Searching(
          'for drive documents'
        ),
      });
    },
    onSettled: () => {
      createEvent(
        fromUserPage
          ? analytics.user_management.user_details.drive.user_drive_search
          : analytics.drive.drive_search.perform_drive_search,
        {
          module: fromUserPage
            ? ModuleList.modules.userManagement.name
            : ModuleList.modules.drive.name,
          section: fromUserPage
            ? ModuleList.modules.userManagement.sections.driveSearch
            : ModuleList.modules.drive.sections.driveSearch,
        }
      );
    },
  });

  // handle loading
  useEffect(() => {
    if (isPending) {
      setSearchedDocs([]);
      setIsLoading(true);
      setErroredUsers([]);
      setNumberOfSearchedUsers(0);
    }
  }, [isPending]);

  // cleanup for when userEmail changes
  useEffect(() => {
    setSearchedDocs([]);
    setIsLoading(false);
    setErroredUsers([]);
    setNumberOfSearchedUsers(0);
    reset(defaultValues);
  }, [reset, userEmail, defaultValues]);

  const firebaseCallback = useCallback(
    (message: FirebaseMessage) => {
      const jsonString = message.message;

      if (!isLoading) {
        return;
      }

      if (jsonString.toLocaleLowerCase().includes('error processing user')) {
        // error message: "Error processing user user9@demo.promevo.com"
        const userEmail = jsonString.split(' ')[3];
        setErroredUsers((prev) => [...prev, userEmail]);
      } else if (jsonString === 'finished user') {
        setNumberOfSearchedUsers((prev) => prev + 1);
      } else if (jsonString === 'finished') {
        setIsLoading(false);
        setSnackbar({
          open: true,
          severity: 'info',
          message: 'Drive search complete.',
        });
      } else if (jsonString === 'errored') {
        // not sure what case this is for?
      } else {
        const parsedDoc: Doc = JSON.parse(jsonString);
        setSearchedDocs((prev) => [...prev, parsedDoc]);
      }
    },
    [setSnackbar, isLoading]
  );

  useFirebaseMessageHandler(
    [firebaseMessageType],
    firebaseUser.firebaseDbPath,
    firebaseCallback
  );

  const isOnlyOwnedByMe = watch('onlyOwnedByMe') || false;

  const driveSearchContent = (
    <Stack sx={{ height: '100%' }} gap={2} flexDirection={'row'}>
      <DriveSearchGridFilters
        control={control}
        watch={watch}
        errors={errors}
        setValue={setValue}
        trigger={trigger}
        getValues={getValues}
        isLoading={isLoading}
        setIsLoading={setIsLoading}
        fromUserPage={fromUserPage}
        userEmail={userEmail}
      />
      <SharedErrorBoundary section="Drive Search Grid">
        <DriveSearchGrid
          clickedDoc={clickedDoc}
          loggedInUser={loggedInUser}
          setClickedDoc={setClickedDoc}
          changeLogRoles={changeLogRoles}
          driveRoles={driveRoles}
          primaryDomain={primaryDomain}
          searchedDocs={searchedDocs}
          driveSearchFilter={getValues()}
          isOnlyOwnedFilterActive={isOnlyOwnedByMe}
          setSearchedDocs={setSearchedDocs}
          isLoading={isLoading}
          setIsLoading={setIsLoading}
          handleSubmit={handleSubmit}
          mutate={mutate}
          erroredUsers={erroredUsers}
          numberOfSearchedUsers={numberOfSearchedUsers}
          fromUserPage={fromUserPage}
          firebaseMessageType={firebaseMessageType}
        />
      </SharedErrorBoundary>
    </Stack>
  );

  return fromUserPage ? (
    <>{driveSearchContent}</>
  ) : (
    <SimpleBreadcrumbsPage
      breadcrumbs={[
        { label: ModuleList.modules.drive.name },
        { label: pagesConfig.driveSearch.name },
      ]}
      bookmarkKey={pagesConfig.driveSearch.value}
    >
      {driveSearchContent}
    </SimpleBreadcrumbsPage>
  );
}
